import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  FileText, Download, Calendar, Filter, RefreshCw, Truck, Package,
  MapPin, CheckCircle2, Clock, DollarSign, Users, Route
} from 'lucide-react';

const LogisticsReports = () => {
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [selectedVehicle, setSelectedVehicle] = useState('all');

  const reportTypes = [
    {
      id: 'delivery-summary',
      name: 'Delivery Summary Report',
      description: 'Complete summary of all deliveries with status, times, and costs',
      icon: Package,
      category: 'Operations',
      frequency: 'Daily, Weekly, Monthly'
    },
    {
      id: 'route-performance',
      name: 'Route Performance Report',
      description: 'Analysis of route efficiency, distance, time, and cost',
      icon: Route,
      category: 'Performance',
      frequency: 'Weekly, Monthly'
    },
    {
      id: 'driver-performance',
      name: 'Driver Performance Report',
      description: 'Individual driver metrics including on-time rate, safety, and efficiency',
      icon: Users,
      category: 'Performance',
      frequency: 'Monthly'
    },
    {
      id: 'fleet-utilization',
      name: 'Fleet Utilization Report',
      description: 'Vehicle usage, availability, and maintenance schedules',
      icon: Truck,
      category: 'Fleet',
      frequency: 'Weekly, Monthly'
    },
    {
      id: 'cost-analysis',
      name: 'Cost Analysis Report',
      description: 'Breakdown of logistics costs by category (fuel, maintenance, labor)',
      icon: DollarSign,
      category: 'Financial',
      frequency: 'Monthly'
    },
    {
      id: 'epod-report',
      name: 'ePOD Report',
      description: 'Electronic proof of delivery records with signatures and photos',
      icon: CheckCircle2,
      category: 'Operations',
      frequency: 'As Needed'
    },
    {
      id: 'chain-of-custody',
      name: 'Chain of Custody Report',
      description: 'Complete handoff records with GPS verification and timestamps',
      icon: MapPin,
      category: 'Compliance',
      frequency: 'As Needed'
    },
    {
      id: 'equipment-tracking',
      name: 'Equipment Tracking Report',
      description: 'Non-inventory equipment moves with condition reports',
      icon: Package,
      category: 'Equipment',
      frequency: 'As Needed'
    },
  ];

  const handleGenerateReport = (reportId) => {
    alert(`Generating ${reportTypes.find(r => r.id === reportId)?.name}... In production, this would generate a PDF report.`);
  };

  const categories = [...new Set(reportTypes.map(r => r.category))];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Logistics Reports</h2>
            <p className="text-sm text-gray-600 mt-1">Generate industry-standard logistics reports</p>
          </div>
          <div className="flex gap-3">
            <select
              value={selectedVehicle}
              onChange={(e) => setSelectedVehicle(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="all">All Vehicles</option>
              <option value="VH-001">VH-001</option>
              <option value="VH-002">VH-002</option>
              <option value="VH-003">VH-003</option>
              <option value="VH-004">VH-004</option>
            </select>
            <div className="flex gap-2">
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                placeholder="Start Date"
              />
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                placeholder="End Date"
              />
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            onClick={() => {
              setDateRange({ start: '', end: '' });
              setSelectedVehicle('all');
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Reset Filters
          </button>
          <button
            onClick={() => {
              const lastMonth = new Date();
              lastMonth.setMonth(lastMonth.getMonth() - 1);
              setDateRange({
                start: lastMonth.toISOString().split('T')[0],
                end: new Date().toISOString().split('T')[0]
              });
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
          >
            Last Month
          </button>
          <button
            onClick={() => {
              const lastWeek = new Date();
              lastWeek.setDate(lastWeek.getDate() - 7);
              setDateRange({
                start: lastWeek.toISOString().split('T')[0],
                end: new Date().toISOString().split('T')[0]
              });
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
          >
            Last Week
          </button>
        </div>
      </div>

      {/* Reports by Category */}
      {categories.map((category) => (
        <div key={category} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{category} Reports</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {reportTypes
              .filter(r => r.category === category)
              .map((report) => {
                const Icon = report.icon;
                return (
                  <motion.div
                    key={report.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-md transition-all"
                  >
                    <div className="flex items-start gap-4 mb-3">
                      <div className="p-3 bg-blue-100 rounded-lg">
                        <Icon className="w-6 h-6 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900 mb-1">{report.name}</h4>
                        <p className="text-sm text-gray-600 mb-2">{report.description}</p>
                        <p className="text-xs text-gray-500">Frequency: {report.frequency}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleGenerateReport(report.id)}
                      className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium flex items-center justify-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Generate Report
                    </button>
                  </motion.div>
                );
              })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default LogisticsReports;
